import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Button, TextField, MenuItem, Chip,
  FormControlLabel, Checkbox, Alert, Divider, IconButton, Grid, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
  Select, FormControl, InputLabel, OutlinedInput, ListItemText, Slider, Switch,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Visibility as PreviewIcon,
  Refresh as RefreshIcon,
  Outbox as OutreachIcon,
  OpenInNew as OpenInNewIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Close as CloseIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
} from '@mui/icons-material';
import {
  previewAutoOutreach, startAutoOutreach, listAutoOutreachRuns,
  deleteAutoOutreachRun, getInteractionTemplates, getIndustries, getIndustryGroups, getAiProviders,
} from '../services/api';
import { useDialog } from '../components/DialogProvider';
import AzotteGrid from '../components/AzotteGrid';

import { LEAD_STATUS_OPTIONS } from '../constants/leadStatus';
const STATUS_OPTIONS = LEAD_STATUS_OPTIONS.map(o => o.value);
const VERDICT_OPTIONS = ['CurrentFit', 'FuturePotential', 'NotAFit', 'Unknown'];
const STAGE_OPTIONS = ['FirstContact', 'FollowUp', 'Qualification', 'ReEngagement', 'DemoInvite', 'Negotiation', 'Closed'];
const SEND_MODE_OPTIONS = [
  { value: 'Schedule', label: 'Schedule (recommended) — uses worker' },
  { value: 'Draft', label: 'Save as Drafts only' },
  { value: 'SendNow', label: 'Send immediately on step 1' },
];

const STATUS_CHIP_COLOR = {
  Pending: 'default',
  Running: 'info',
  Cancelling: 'warning',
  Completed: 'success',
  Failed: 'error',
  Cancelled: 'warning',
};

const defaultFilter = {
  leadIdsText: '',
  idFrom: '',
  idTo: '',
  industryIds: [],
  industryGroupIds: [],
  countries: '',
  countryCodes: '',
  verdicts: [],
  statuses: ['Qualified'],
  onlyDiscoveredContacts: true,
  onlyWithContactEmail: true,
  minOutreachScore: '',
  excludeAlreadyContactedDays: '',
  limit: '',
};

function parseIdList(text) {
  if (!text) return null;
  const ids = text.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean)
    .map((s) => parseInt(s, 10)).filter((n) => Number.isInteger(n) && n > 0);
  return ids.length > 0 ? ids : null;
}
function parseStringList(text) {
  if (!text) return null;
  const arr = text.split(/[,;]+/).map((s) => s.trim()).filter(Boolean);
  return arr.length > 0 ? arr : null;
}
function buildFilter(f) {
  return {
    leadIds: parseIdList(f.leadIdsText),
    idFrom: f.idFrom ? parseInt(f.idFrom, 10) : null,
    idTo: f.idTo ? parseInt(f.idTo, 10) : null,
    industryIds: f.industryIds && f.industryIds.length > 0 ? f.industryIds : null,
    industryGroupIds: f.industryGroupIds && f.industryGroupIds.length > 0 ? f.industryGroupIds : null,
    countries: parseStringList(f.countries),
    countryCodes: parseStringList(f.countryCodes),
    verdicts: f.verdicts && f.verdicts.length > 0 ? f.verdicts : null,
    statuses: f.statuses && f.statuses.length > 0 ? f.statuses : null,
    onlyDiscoveredContacts: !!f.onlyDiscoveredContacts,
    onlyWithContactEmail: !!f.onlyWithContactEmail,
    minOutreachScore: f.minOutreachScore ? parseInt(f.minOutreachScore, 10) : null,
    excludeAlreadyContactedDays: f.excludeAlreadyContactedDays ? parseInt(f.excludeAlreadyContactedDays, 10) : null,
    limit: f.limit ? parseInt(f.limit, 10) : null,
  };
}

function fmtSecs(s) { if (s < 60) return `${s}s`; const m = Math.floor(s / 60); return `${m}m ${s % 60}s`; }
function fmtDuration(start, end, accumulatedSeconds) {
  const acc = accumulatedSeconds || 0;
  if (end) {
    if (acc > 0) return fmtSecs(acc);
    if (start) return fmtSecs(Math.max(0, Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000)));
    return '—';
  }
  if (!start) return acc > 0 ? fmtSecs(acc) : '—';
  return fmtSecs(acc + Math.max(0, Math.round((Date.now() - new Date(start).getTime()) / 1000)));
}

const defaultSequence = [
  { templateId: '', delayDays: 0, stage: 'FirstContact' },
  { templateId: '', delayDays: 5, stage: 'FollowUp' },
  { templateId: '', delayDays: 10, stage: 'FollowUp' },
];

export default function AutoEmailOutreachPage() {
  const navigate = useNavigate();
  const dialog = useDialog();
  const [filter, setFilter] = useState(defaultFilter);
  const [industries, setIndustries] = useState([]);
  const [industryGroups, setIndustryGroups] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [providers, setProviders] = useState([]);
  const [aiProviderName, setAiProviderName] = useState('');
  const [sequence, setSequence] = useState(defaultSequence);
  const [maxEmailsPerLead, setMaxEmailsPerLead] = useState('3');
  const [sendMode, setSendMode] = useState('Schedule');
  const [usePersonalize, setUsePersonalize] = useState(true);
  const [useAutoImprove, setUseAutoImprove] = useState(true);
  const [autoUseRecommendedSendTime, setAutoUseRecommendedSendTime] = useState(true);
  const [defaultScheduledFor, setDefaultScheduledFor] = useState('');
  const [qualityScoreThreshold, setQualityScoreThreshold] = useState(60);
  const [intervalSeconds, setIntervalSeconds] = useState('5');
  const [preview, setPreview] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState({ preview: false, start: false });
  const [error, setError] = useState('');
  const [filterOpen, setFilterOpen] = useState(true);
  const [newDialogOpen, setNewDialogOpen] = useState(false);

  const activeFilterCount = [
    parseIdList(filter.leadIdsText), filter.idFrom, filter.idTo,
    filter.industryIds && filter.industryIds.length > 0 ? '1' : null,
    filter.industryGroupIds && filter.industryGroupIds.length > 0 ? '1' : null,
    filter.countries, filter.countryCodes,
    filter.verdicts && filter.verdicts.length > 0 ? '1' : null,
    (filter.statuses && (filter.statuses.length !== 1 || filter.statuses[0] !== 'Qualified')) ? '1' : null,
    filter.minOutreachScore, filter.excludeAlreadyContactedDays, filter.limit,
  ].filter(Boolean).length
    + (filter.onlyDiscoveredContacts ? 0 : 1)
    + (filter.onlyWithContactEmail ? 0 : 1);

  const loadRecent = useCallback(async () => {
    try { const { data } = await listAutoOutreachRuns(20); setRecent(data || []); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => {
      try { const { data } = await getInteractionTemplates({ channel: 'Email' }); setTemplates(Array.isArray(data) ? data : (data?.items || [])); } catch { /* ignore */ }
      try { const { data } = await getIndustries(); setIndustries(Array.isArray(data) ? data : (data?.items || [])); } catch { /* ignore */ }
      try { const { data } = await getIndustryGroups(); setIndustryGroups(Array.isArray(data) ? data : (data?.items || [])); } catch { /* ignore */ }
      try {
        const { data } = await getAiProviders();
        setProviders(Array.isArray(data) ? data : []);
      } catch { /* ignore */ }
    })();
    loadRecent();
    const t = setInterval(loadRecent, 5000);
    return () => clearInterval(t);
  }, [loadRecent]);

  const handlePreview = async () => {
    if (!sequence[0]?.templateId) { setError('Pick a template for step 1.'); return; }
    setLoading((s) => ({ ...s, preview: true }));
    setError('');
    try {
      const { data } = await previewAutoOutreach({
        filter: buildFilter(filter),
        templateId: parseInt(sequence[0].templateId, 10),
        sequenceLength: sequence.filter((s) => s.templateId).length || 1,
        maxEmailsPerLead: parseInt(maxEmailsPerLead, 10) || 3,
      });
      setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Preview failed');
    } finally { setLoading((s) => ({ ...s, preview: false })); }
  };

  const handleStart = async () => {
    const filledSequence = sequence.filter((s) => s.templateId);
    if (filledSequence.length === 0) { setError('Add at least one step with a template.'); return; }
    const interval = parseInt(intervalSeconds, 10) || 5;
    const winners = parseInt(maxEmailsPerLead, 10) || 3;
    const expected = preview?.expectedTotalEmails ?? '?';
    const msg = `Start outreach for ${preview?.contactsToEmailCount ?? '?'} contacts across ${filledSequence.length} step(s) → ~${expected} emails total. Send mode: ${sendMode}. Continue?`;
    if (!(await dialog.confirm({ title: 'Start auto outreach', message: msg, confirmText: 'Start' }))) return;

    setLoading((s) => ({ ...s, start: true }));
    setError('');
    try {
      const { data } = await startAutoOutreach({
        filter: buildFilter(filter),
        templateId: parseInt(filledSequence[0].templateId, 10),
        sequence: filledSequence.map((s) => ({
          templateId: parseInt(s.templateId, 10),
          delayDays: parseInt(s.delayDays, 10) || 0,
          stage: s.stage,
        })),
        maxEmailsPerLead: winners,
        sendMode,
        aiProviderName: aiProviderName || null,
        usePersonalize,
        useAutoImprove,
        autoUseRecommendedSendTime,
        defaultScheduledFor: defaultScheduledFor ? new Date(defaultScheduledFor).toISOString() : null,
        qualityScoreThreshold,
        intervalSeconds: interval,
      });
      navigate(`/leads/auto-outreach/${data.id}`);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to start outreach job');
    } finally { setLoading((s) => ({ ...s, start: false })); }
  };

  const handleDeleteRun = async (row) => {
    const ok = await dialog.confirm({
      title: 'Delete outreach job',
      message: `Delete job #${row.id}? Already-scheduled emails are NOT auto-cancelled.`,
      confirmText: 'Delete', danger: true,
    });
    if (!ok) return;
    try { await deleteAutoOutreachRun(row.id); await loadRecent(); }
    catch (e) { setError(e?.response?.data?.message || 'Delete failed'); }
  };

  const updateStep = (idx, patch) => {
    setSequence((seq) => seq.map((s, i) => i === idx ? { ...s, ...patch } : s));
  };
  const moveStep = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= sequence.length) return;
    setSequence((seq) => { const arr = [...seq]; [arr[idx], arr[j]] = [arr[j], arr[idx]]; return arr; });
  };
  const removeStep = (idx) => setSequence((seq) => seq.filter((_, i) => i !== idx));
  const addStep = () => setSequence((seq) => seq.length >= 5 ? seq : [...seq, { templateId: '', delayDays: 5, stage: 'FollowUp' }]);

  const previewColumns = [
    { key: 'id', header: 'ID', render: (v) => `#${v}` },
    { key: 'companyName', header: 'Company', render: (v) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{v}</Typography> },
    { key: 'status', header: 'Status', hideOnMobile: true },
    { key: 'aiIcpFit', header: 'Verdict', hideOnMobile: true, render: (v) => v || '—' },
    { key: 'country', header: 'Country', hideOnMobile: true, render: (v) => v || '—' },
    { key: 'recommendedContactCount', header: 'Winners', render: (v) => v ?? 0 },
    { key: 'topOutreachScore', header: 'Top score', render: (v) => v ?? '—' },
  ];

  const recentColumns = [
    { key: 'id', header: 'ID', render: (v) => <Typography sx={{ fontWeight: 600 }}>#{v}</Typography> },
    { key: 'status', header: 'Status', render: (v) => <Chip size="small" label={v} color={STATUS_CHIP_COLOR[v] || 'default'} sx={{ fontWeight: 600 }} /> },
    { key: 'sendMode', header: 'Mode', hideOnMobile: true },
    { key: 'templateName', header: 'Template', hideOnMobile: true, render: (v) => v || '—' },
    { key: 'maxEmailsPerLead', header: 'Max/Lead', hideOnMobile: true, hideOnTablet: true },
    {
      key: 'startedAt', header: 'Started', hideOnMobile: true,
      render: (v) => v ? new Date(v).toLocaleString() : '—',
    },
    {
      key: 'duration', header: 'Duration', hideOnMobile: true,
      render: (_, row) => fmtDuration(row.startedAt, row.finishedAt, row.accumulatedSeconds),
    },
    { key: 'totalLeads', header: 'Leads', hideOnMobile: true, hideOnTablet: true },
    { key: 'processedLeads', header: 'Done' },
    {
      key: 'emailsScheduledTotal', header: 'Emails', hideOnMobile: true,
      render: (v) => <Box component="span" sx={{ color: '#0284c7', fontWeight: 700 }}>{v ?? 0}</Box>,
    },
    {
      key: 'emailsNeedReviewTotal', header: 'NeedsReview', hideOnMobile: true, hideOnTablet: true,
      render: (v) => <Box component="span" sx={{ color: v > 0 ? '#f59e0b' : '#94a3b8', fontWeight: 600 }}>{v ?? 0}</Box>,
    },
    {
      key: 'createdByUsername', header: 'By', hideOnMobile: true, hideOnTablet: true,
      render: (v) => v || '—',
    },
    {
      key: 'actions', header: '',
      render: (_, row) => {
        const canDelete = ['Completed', 'Cancelled', 'Failed'].includes(row.status);
        return (
          <Stack direction="row" spacing={0.75}>
            <Button size="small" variant="outlined"
              startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              onClick={() => navigate(`/leads/auto-outreach/${row.id}`)}
              sx={{ textTransform: 'none', borderRadius: 1.5 }}>View</Button>
            {canDelete && (
              <Tooltip title="Delete job">
                <IconButton size="small" color="error" onClick={() => handleDeleteRun(row)}><DeleteIcon fontSize="small" /></IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ];

  const renderMultiChips = (selected) =>
    Array.isArray(selected) && selected.length > 0
      ? <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
          {selected.map((s) => <Chip key={s} size="small" label={s} sx={{ height: 20 }} />)}
        </Box>
      : <em style={{ color: '#94a3b8' }}>— Any —</em>;

  return (
    <Box>
      <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Typography sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          <OutreachIcon color="primary" /> Auto Email Outreach
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>
          Send personalized emails to discovered winner contacts across qualified leads. Linear sequences, AI quality scoring, send-time recommendation.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }}>
        <Button variant="contained" size="medium" startIcon={<AddIcon />} onClick={() => setNewDialogOpen(true)}
          sx={{
            textTransform: 'none', borderRadius: 1.5, fontWeight: 600,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            boxShadow: '0 2px 6px rgba(99,102,241,0.35)',
            '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' },
          }}>
          New outreach job
        </Button>
        {activeFilterCount > 0 && (
          <Chip size="small" label={`${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} set`}
            onClick={() => setNewDialogOpen(true)}
            sx={{ bgcolor: '#eef2ff', color: '#4338ca', fontWeight: 600, cursor: 'pointer' }} />
        )}
      </Stack>

      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
          <OutreachIcon color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>New auto outreach job</Typography>
            <Typography variant="caption" color="text.secondary">Filter leads, build sequence, configure AI, start.</Typography>
          </Box>
          <IconButton onClick={() => setNewDialogOpen(false)} size="small"><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ borderBottom: '1px solid #e2e8f0' }}>
            <Stack direction="row" alignItems="center" spacing={1}
              onClick={() => setFilterOpen((o) => !o)}
              sx={{ px: 2, py: 1.25, cursor: 'pointer', userSelect: 'none', bgcolor: filterOpen ? '#f8fafc' : 'transparent', '&:hover': { bgcolor: '#f8fafc' } }}>
              <FilterListIcon sx={{ fontSize: 18, color: '#475569' }} />
              <Typography variant="subtitle2" sx={{ color: '#0f172a', fontWeight: 600 }}>Filter leads</Typography>
              {activeFilterCount > 0 && <Chip size="small" label={activeFilterCount} sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#eef2ff', color: '#4338ca' }} />}
              <Box sx={{ flex: 1 }} />
              <IconButton size="small" sx={{ transform: filterOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}><ExpandMoreIcon fontSize="small" /></IconButton>
            </Stack>
            <Collapse in={filterOpen} unmountOnExit>
              <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField fullWidth size="small" label="Lead IDs (comma-separated, optional)"
                      value={filter.leadIdsText} onChange={(e) => setFilter((f) => ({ ...f, leadIdsText: e.target.value }))}
                      helperText="e.g. 1024, 1037 — leave empty to use other filters" />
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth size="small" type="number" label="ID from"
                    value={filter.idFrom} onChange={(e) => setFilter((f) => ({ ...f, idFrom: e.target.value }))} /></Grid>
                  <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth size="small" type="number" label="ID to"
                    value={filter.idTo} onChange={(e) => setFilter((f) => ({ ...f, idTo: e.target.value }))} /></Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Statuses (default Qualified)</InputLabel>
                      <Select multiple value={filter.statuses}
                        onChange={(e) => setFilter((f) => ({ ...f, statuses: e.target.value }))}
                        input={<OutlinedInput label="Statuses (default Qualified)" />}
                        renderValue={renderMultiChips}>
                        {STATUS_OPTIONS.map((s) => (
                          <MenuItem key={s} value={s}>
                            <Checkbox size="small" checked={filter.statuses.includes(s)} />
                            <ListItemText primary={s} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Verdict</InputLabel>
                      <Select multiple value={filter.verdicts}
                        onChange={(e) => setFilter((f) => ({ ...f, verdicts: e.target.value }))}
                        input={<OutlinedInput label="Verdict" />}
                        renderValue={renderMultiChips}>
                        {VERDICT_OPTIONS.map((v) => (
                          <MenuItem key={v} value={v}>
                            <Checkbox size="small" checked={filter.verdicts.includes(v)} />
                            <ListItemText primary={v} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Industry groups</InputLabel>
                      <Select multiple value={filter.industryGroupIds}
                        onChange={(e) => setFilter((f) => ({ ...f, industryGroupIds: e.target.value }))}
                        input={<OutlinedInput label="Industry groups" />}
                        renderValue={(sel) => sel.length > 0
                          ? <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                              {sel.map((id) => { const g = industryGroups.find((x) => x.id === id); return <Chip key={id} size="small" label={g?.name || `#${id}`} sx={{ height: 20 }} />; })}
                            </Box>
                          : <em style={{ color: '#94a3b8' }}>— Any —</em>}>
                        {industryGroups.map((g) => (
                          <MenuItem key={g.id} value={g.id}>
                            <Checkbox size="small" checked={filter.industryGroupIds.includes(g.id)} />
                            <ListItemText primary={g.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Industries</InputLabel>
                      <Select multiple value={filter.industryIds}
                        onChange={(e) => setFilter((f) => ({ ...f, industryIds: e.target.value }))}
                        input={<OutlinedInput label="Industries" />}
                        renderValue={(sel) => sel.length > 0
                          ? <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                              {sel.map((id) => { const ind = industries.find((i) => i.id === id); return <Chip key={id} size="small" label={ind?.name || `#${id}`} sx={{ height: 20 }} />; })}
                            </Box>
                          : <em style={{ color: '#94a3b8' }}>— Any —</em>}>
                        {industries.map((ind) => (
                          <MenuItem key={ind.id} value={ind.id}>
                            <Checkbox size="small" checked={filter.industryIds.includes(ind.id)} />
                            <ListItemText primary={ind.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth size="small" label="Countries (comma-separated)"
                      value={filter.countries} onChange={(e) => setFilter((f) => ({ ...f, countries: e.target.value }))}
                      helperText="e.g. United States, Germany" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth size="small" label="Country codes (comma-separated)"
                      value={filter.countryCodes} onChange={(e) => setFilter((f) => ({ ...f, countryCodes: e.target.value }))}
                      helperText="e.g. US, DE" />
                  </Grid>

                  <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth size="small" type="number" label="Min OutreachScore"
                    value={filter.minOutreachScore} onChange={(e) => setFilter((f) => ({ ...f, minOutreachScore: e.target.value }))} /></Grid>
                  <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth size="small" type="number" label="Skip recently contacted (days)"
                    value={filter.excludeAlreadyContactedDays} onChange={(e) => setFilter((f) => ({ ...f, excludeAlreadyContactedDays: e.target.value }))} /></Grid>
                  <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth size="small" type="number" label="Limit"
                    inputProps={{ min: 1, max: 5000 }} value={filter.limit}
                    onChange={(e) => setFilter((f) => ({ ...f, limit: e.target.value }))} /></Grid>
                </Grid>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 2 }}>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={filter.onlyDiscoveredContacts}
                      onChange={(e) => setFilter((f) => ({ ...f, onlyDiscoveredContacts: e.target.checked }))} />}
                    label={<Typography variant="body2">Only leads with contacts already discovered</Typography>} />
                  <FormControlLabel
                    control={<Checkbox size="small" checked={filter.onlyWithContactEmail}
                      onChange={(e) => setFilter((f) => ({ ...f, onlyWithContactEmail: e.target.checked }))} />}
                    label={<Typography variant="body2">Only leads with contact email available</Typography>} />
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1, color: '#475569' }}>Sequence (linear, max 5 steps)</Typography>
                {sequence.map((step, idx) => (
                  <Stack key={idx} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 1 }}>
                    <Chip size="small" label={`Step ${idx + 1}`} sx={{ minWidth: 64 }} />
                    <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
                      <InputLabel>Template</InputLabel>
                      <Select value={step.templateId} onChange={(e) => updateStep(idx, { templateId: e.target.value })} label="Template">
                        <MenuItem value="">— Pick template —</MenuItem>
                        {templates.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField size="small" type="number" label="Delay (days)" sx={{ width: 130 }}
                      value={step.delayDays} onChange={(e) => updateStep(idx, { delayDays: e.target.value })}
                      inputProps={{ min: 0, max: 90 }} disabled={idx === 0} />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>Stage</InputLabel>
                      <Select value={step.stage} onChange={(e) => updateStep(idx, { stage: e.target.value })} label="Stage">
                        {STAGE_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton size="small" onClick={() => moveStep(idx, -1)} disabled={idx === 0}><UpIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => moveStep(idx, 1)} disabled={idx === sequence.length - 1}><DownIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => removeStep(idx)} disabled={sequence.length <= 1}><DeleteIcon fontSize="small" /></IconButton>
                    </Stack>
                  </Stack>
                ))}
                <Button size="small" startIcon={<AddIcon />} onClick={addStep} disabled={sequence.length >= 5}
                  sx={{ textTransform: 'none' }}>Add step</Button>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#475569' }}>AI &amp; pacing</Typography>
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth size="small" type="number" label="Max emails/lead"
                    inputProps={{ min: 1, max: 10 }} value={maxEmailsPerLead}
                    onChange={(e) => setMaxEmailsPerLead(e.target.value)} /></Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Send mode</InputLabel>
                      <Select value={sendMode} onChange={(e) => setSendMode(e.target.value)} label="Send mode">
                        {SEND_MODE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>AI engine</InputLabel>
                      <Select value={aiProviderName} onChange={(e) => setAiProviderName(e.target.value)} label="AI engine">
                        <MenuItem value="">— Default —</MenuItem>
                        {providers.map((p) => <MenuItem key={p.name} value={(p.name || '').toLowerCase()}>{p.displayName || p.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth size="small" type="number" label="Interval (sec)"
                    inputProps={{ min: 0, max: 3600 }} value={intervalSeconds}
                    onChange={(e) => setIntervalSeconds(e.target.value)} helperText="Per-lead pacing" /></Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>Quality score threshold (below = NeedsReview)</Typography>
                    <Slider value={qualityScoreThreshold} onChange={(_, v) => setQualityScoreThreshold(v)} min={0} max={100} valueLabelDisplay="on" />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                      <FormControlLabel control={<Switch size="small" checked={usePersonalize} onChange={(e) => setUsePersonalize(e.target.checked)} />} label="Personalize" />
                      <FormControlLabel control={<Switch size="small" checked={useAutoImprove} onChange={(e) => setUseAutoImprove(e.target.checked)} />} label="Auto Improve" />
                      <FormControlLabel control={<Switch size="small" checked={autoUseRecommendedSendTime} onChange={(e) => setAutoUseRecommendedSendTime(e.target.checked)} />} label="AI send time" />
                    </Stack>
                  </Grid>
                  {!autoUseRecommendedSendTime && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth size="small" type="datetime-local" label="Default scheduled-for (UTC)"
                        InputLabelProps={{ shrink: true }} value={defaultScheduledFor}
                        onChange={(e) => setDefaultScheduledFor(e.target.value)} />
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Collapse>
          </Box>
          {preview && (
            <Box sx={{ p: { xs: 2, md: 2.5 } }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#475569' }}>
                Preview: <strong>{preview.matchCount}</strong> leads match · <strong>{preview.contactsToEmailCount}</strong> contacts to email · <strong>{preview.expectedTotalEmails}</strong> total emails
              </Typography>
              {preview.sample && preview.sample.length > 0 ? (
                <AzotteGrid columns={previewColumns} rows={preview.sample}
                  totalCount={preview.sample.length} showPagination={false} showToolbar={false} />
              ) : <Typography variant="body2" sx={{ color: '#94a3b8' }}>No leads match the filter.</Typography>}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: 'wrap', '& .MuiButton-root': { borderRadius: 1.5, textTransform: 'none' } }}>
          <Button size="small" onClick={() => { setFilter(defaultFilter); setSequence(defaultSequence); setPreview(null); }}>Reset</Button>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={() => setNewDialogOpen(false)}>Cancel</Button>
          <Button variant="outlined" size="small" startIcon={<PreviewIcon />} onClick={handlePreview} disabled={loading.preview}>
            {loading.preview ? 'Counting…' : 'Preview match'}
          </Button>
          <Button variant="contained" size="small" startIcon={<PlayIcon />} onClick={handleStart} disabled={loading.start}
            sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' } }}>
            {loading.start ? 'Starting…' : 'Start outreach'}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ color: '#475569', flex: 1 }}>Recent runs</Typography>
          <IconButton size="small" onClick={loadRecent} title="Refresh"><RefreshIcon fontSize="small" /></IconButton>
        </Stack>
        <AzotteGrid columns={recentColumns} rows={recent}
          totalCount={recent.length} showPagination={false} showToolbar={false}
          onDoubleClick={(r) => navigate(`/leads/auto-outreach/${r.id}`)}
          actionColumnKey="actions" />
      </Paper>
    </Box>
  );
}
